import React from "react";
import { useTranslation } from "react-i18next";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const VisaBadge = () => (
  <svg width="32" height="22" viewBox="0 0 38 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="37" height="21" rx="4.5" fill="#1A1F71" stroke="#1A1F71" />
    <text
      x="19"
      y="15"
      textAnchor="middle"
      fontFamily="Arial, sans-serif"
      fontSize="9.5"
      fontWeight="700"
      fontStyle="italic"
      letterSpacing="0.3"
      fill="#ffffff"
    >
      VISA
    </text>
  </svg>
);

const MastercardBadge = () => (
  <svg width="32" height="22" viewBox="0 0 38 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="37" height="21" rx="4.5" fill="#FFFFFF" stroke="#E5E7EB" />
    <circle cx="16.5" cy="11" r="6" fill="#EB001B" />
    <circle cx="21.5" cy="11" r="6" fill="#F79E1B" />
    <path d="M19 6.3a6 6 0 0 1 0 9.4 6 6 0 0 1 0-9.4Z" fill="#FF5F00" />
  </svg>
);

const ApplePayBadge = () => (
  <svg width="32" height="22" viewBox="0 0 38 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="37" height="21" rx="4.5" fill="#000000" stroke="#000000" />
    {/* generic "flash/pay" glyph — not Apple's logo */}
    <path d="M9 12.1h2l-.6 2.9 3.4-4h-2l.6-2.9-3.4 4Z" fill="#ffffff" />
    <text
      x="24"
      y="14.5"
      textAnchor="middle"
      fontFamily="Arial, sans-serif"
      fontSize="8"
      fontWeight="600"
      fill="#ffffff"
    >
      Pay
    </text>
  </svg>
);

const GooglePayBadge = () => (
  <svg width="32" height="22" viewBox="0 0 38 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="37" height="21" rx="4.5" fill="#FFFFFF" stroke="#E5E7EB" />
    <circle cx="9.5" cy="11" r="4" fill="none" stroke="#4285F4" strokeWidth="1.6" />
    <text
      x="24"
      y="14.5"
      textAnchor="middle"
      fontFamily="Arial, sans-serif"
      fontSize="7.5"
      fontWeight="600"
      fill="#5F6368"
    >
      Pay
    </text>
  </svg>
);

function StripePay({ checkoutWithStripePay, isStripePaySelected }) {
  const { t } = useTranslation();

  const handleSelect = () => checkoutWithStripePay({ selected: true, payment_by: "stripe_payment" });

  return (
    <div
      style={{ marginBottom: "10px" }}
      className={`stripe-pay-card ${isStripePaySelected ? "stripe-pay-card--selected" : ""}`}
      onClick={handleSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleSelect();
        }
      }}
    >
      <div className="stripe-pay-card__top">
        <div className="stripe-pay-card__left">
          <div className="stripe-pay-card__radio">
            <span className={`stripe-pay-card__radio-dot ${isStripePaySelected ? "is-checked" : ""}`} />
          </div>

          <CreditCardOutlinedIcon className="stripe-pay-card__icon" style={{ fontSize: "1.2rem" }} />

          <div className="stripe-pay-card__text">
            <span className="stripe-pay-card__title">{t("Credit / Debit Card")}</span>
            <span
              className="stripe-pay-card__subtitle"
              style={{ gap: "5px", display: "flex", alignItems: "center", lineHeight: 1.1 }}
            >
              Stripe {t("Pay securely")}
            </span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          {isStripePaySelected && (
            <CheckCircleIcon className="stripe-pay-card__check" style={{ fontSize: "1.15rem" }} />
          )}
        </div>
      </div>

      <div className="stripe-pay-card__methods">
        <VisaBadge />
        <MastercardBadge />
        <ApplePayBadge />
        <GooglePayBadge />
      </div>

      <style>{`
        .stripe-pay-card {
          display: flex;
          flex-direction: column;
          gap: 5px;
          width: 100%;
          padding: 14px 18px;
          border: 1.5px solid #e5e7eb;
          border-radius: 12px;
          background: #ffffff;
          cursor: pointer;
          transition: border-color 0.15s ease, background 0.15s ease;
          outline: none;
          box-sizing: border-box;
        }

        .stripe-pay-card:hover {
          border-color: #d1d5db;
        }

        .stripe-pay-card:focus-visible {
          border-color: #d0b77c;
          box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.12);
        }

        .stripe-pay-card--selected {
          border-color: #d0b77c;
          background: #fffaf5;
        }

        .stripe-pay-card__top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .stripe-pay-card__left {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .stripe-pay-card__radio {
          flex-shrink: 0;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          border: 2px solid #d1d5db;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .stripe-pay-card--selected .stripe-pay-card__radio {
          border-color: #d0b77c;
        }

        .stripe-pay-card__radio-dot {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: transparent;
        }

        .stripe-pay-card__radio-dot.is-checked {
          background: #d0b77c;
        }

        .stripe-pay-card__icon {
          flex-shrink: 0;
          color: #6b7280;
        }

        .stripe-pay-card--selected .stripe-pay-card__icon {
          color: #d0b77c;
        }

        .stripe-pay-card__text {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .stripe-pay-card__title {
          font-size: 14px;
          font-weight: 600;
          color: #111827;
        }

        .stripe-pay-card__subtitle {
          font-size: 12.5px;
          color: #9ca3af;
          margin-top: 1px;
        }

        .stripe-pay-card__check {
          color: #d0b77c;
          flex-shrink: 0;
        }

        .stripe-pay-card__methods {
          display: flex;
          align-items: center;
          gap: 7px;
          flex-wrap: wrap;
          padding-left: 32px; /* aligns under the title text */
          justify-content: right
        }

        .stripe-pay-card__methods svg {
          flex-shrink: 0;
          border-radius: 4.5px;
          box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.02);
        }

        @media (max-width: 560px) {
          .stripe-pay-card {
            padding: 12px 14px;
            gap: 5px;
          }
          .stripe-pay-card__title {
            font-size: 14px;
          }
          .stripe-pay-card__subtitle {
            display: none;
          }
          .stripe-pay-card__methods {
            padding-left: 0;
            gap: 6px;
          }
          .stripe-pay-card__methods svg {
            width: 34px;
            height: 19px;
          }
        }

        @media (max-width: 360px) {
          .stripe-pay-card__methods svg {
            width: 31px;
            height: 17px;
          }
        }
      `}</style>
    </div>
  );
}

export default StripePay;
